import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SignUp, SignIn, VerifyEmail } from './components';
import { Navbar } from './components';
import { UserMenu } from './components';
import { Listing, ListingType } from './types/index';
import { supabase } from './lib/supabase';
import { LandingPage, About, FAQ, Contact, Terms, CompleteProfile } from './pages';
import NotificationBar from './components/layout/NotificationBar';
import { HomePage } from './components/HomePage';
import { FindCard } from './components/FindCard';
import './App.css';

// Lazy load heavy components
const LazyAdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const LazyListingForm = lazy(() => import('./components/listings/ListingForm'));
const LazyListingList = lazy(() => import('./components/listings/ListingList'));
const LazyMap = lazy(() => import('./components/listings/Map'));

// Add these new lazy imports
const LazyCategories = lazy(() => import('./pages/Categories'));
const LazyProfile = lazy(() => import('./pages/Profile'));
const LazyListingDetail = lazy(() => import('./pages/ListingDetail'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-3 border-b-3 border-orange-500"></div>
  </div>
);

// Enhanced Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error:', error, errorInfo);
    toast.error('Something went wrong. Please refresh the page.');
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
              >
                Refresh Page
              </button>
              <button 
                onClick={() => this.setState({ hasError: false, error: null })}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
              >
                Try Again
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left text-sm text-red-600 bg-red-50 p-4 rounded">
                <summary className="cursor-pointer">Error Details</summary>
                <pre className="mt-2 whitespace-pre-wrap">{this.state.error.stack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Enhanced Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

// Enhanced Admin Route component with caching
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setIsAdmin(profile?.is_admin || false);
        setError(null);
      } catch (error: any) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin permissions');
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user?.id]);

  if (loading || isAdmin === null) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-red-600 mb-2">Access Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// AppContent component
function AppContent() {
  const { user, loading } = useAuth();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Toaster 
        position="top-center" 
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-text)',
          },
        }}
      />
      
      {loading && (
        <div className="fixed top-4 right-4 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      )}
      
      <NotificationBar />
      <Navbar />
      
      <main className="flex-1">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Use the new HomePage for the landing */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            
            {/* New pages */}
            <Route path="/categories" element={<LazyCategories />} />
            <Route path="/listing/:id" element={<LazyListingDetail />} />
            
            {/* Auth Routes */}
            <Route
              path="/signin"
              element={user ? <Navigate to="/listings" replace /> : <SignIn />}
            />
            <Route
              path="/signup"
              element={user ? <Navigate to="/listings" replace /> : <SignUp />}
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            
            {/* Protected Routes */}
            <Route
              path="/complete-profile"
              element={
                <ProtectedRoute>
                  <CompleteProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <div className="pt-16">
                    <LazyProfile />
                  </div>
                </ProtectedRoute>
              }
            />
            
            {/* CRITICAL: List View Route */}
            <Route
              path="/listings"
              element={
                <div className="pt-16">
                  <LazyListingList />
                </div>
              }
            />
            
            {/* CRITICAL: Map View Route */}
            <Route
              path="/map"
              element={
                <div className="pt-16">
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyMap 
                      listings={[]}
                      selectedListing={null}
                      onListingSelect={() => {}}
                    />
                  </Suspense>
                </div>
              }
            />
            
            <Route
              path="/create"
              element={
                <ProtectedRoute>
                  <div className="pt-16">
                    <LazyListingForm />
                  </div>
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <div className="pt-16">
                    <LazyAdminDashboard />
                  </div>
                </AdminRoute>
              }
            />
            
            {/* 404 Route */}
            <Route 
              path="*" 
              element={
                <div className="flex items-center justify-center min-h-screen">
                  <div className="text-center">
                    <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-white mb-4">
                      404 - Page Not Found
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                      The page you're looking for doesn't exist.
                    </p>
                    <Navigate to="/" replace />
                  </div>
                </div>
              } 
            />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

// Main App component
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ThemeProvider>
          <AuthProvider>
            <ProfileProvider>
              <NotificationProvider>
                <AppContent />
              </NotificationProvider>
            </ProfileProvider>
          </AuthProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App; 